/**
 * Core
 */
import Vue from 'vue';
import Router from 'vue-router';

/**
 * Views
 */
import Customers from './views/Customers.vue';
import Dashboard from './views/Dashboard.vue';
import DriverItems from './views/DriverItems.vue';
import Errors from './views/Errors.vue';
import Login from './views/Login.vue';
import Logout from './views/Logout.vue';
import Materials from './views/Materials.vue';
import MaterialsStock from './views/MaterialsStock.vue';
import MaterialsStockProducts from './views/MaterialsStockProducts.vue';
import MaterialsStockColors from './views/MaterialsStockColors.vue';
import MaterialsStockUsage from './views/MaterialsStockUsage.vue';
import Orders from './views/Orders.vue';
import Polars from './views/Polars.vue';
import Procedures from './views/Procedures.vue';
import ProcessItems from './views/ProcessItems.vue';
import Products from './views/Products.vue';
import Seams from './views/Seams.vue';
import Settings from './views/Settings.vue';
import Sizes from './views/Sizes.vue';
import Splashscreen from './views/SplashScreen.vue';
import Strands from './views/Strands.vue';
import Users from './views/Users.vue';

/**
 * Subviews
 */
import CustomerForm from './components/forms/CustomerForm.vue';
import MaterialForm from './components/forms/MaterialForm.vue';
import MaterialsStockColorsForm from './components/forms/MaterialsStockColorsForm.vue';
import MaterialsStockProductForm from './components/forms/MaterialsStockProductForm.vue';
import NoPermissions from './views/NoPermissions.vue';
import OrderForm from './components/forms/OrderForm.vue';
import PolarForm from './components/forms/PolarForm.vue';
import ProcedureForm from './components/forms/ProcedureForm.vue';
import ProductForm from './components/forms/ProductForm.vue';
import SeamForm from './components/forms/SeamForm.vue';
import SizeForm from './components/forms/SizeForm.vue';
import StrandForm from './components/forms/StrandForm.vue';
import UserForm from './components/forms/UserForm.vue';

/**
 * Helpers
 */
import SanitizeUtils from './utils/SanitizeUtils';

Vue.use(Router);

function crmPermissionsCheck(permissionName, next) {
  if (permissionName === 'dashboard') {
    if (window.crmApp && window.crmApp.hasPermissions('process-items')) {
      next('zadania');
      return;
    } else if (window.crmApp && window.crmApp.hasPermissions('driver_orders')) {
      next('zadania-kierowcy');
      return;
    }
  }

  if (!window.crmApp || !window.crmApp.permissionsAvailable()) {
    next();
    return;
  }

  if (window.crmApp && window.crmApp.hasPermissions(permissionName)) {
    next();
    return;
  }

  next('brak-uprawnien');
}

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  linkActiveClass: 'active',
  linkExactActiveClass: 'exact-active',
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
  routes: [
    // eslint-disable-next-line
    {
      path: '/logowanie',
      component: Login,
      name: 'Logowanie',
      meta: {
        layout: 'guest'
      }
    },
    {
      path: '/wyloguj',
      component: Logout,
      name: 'Wyloguj'
    },
    {
      path: '/wczytywanie',
      component: Splashscreen,
      name: 'Splashscreen'
    },
    {
      path: '/',
      redirect: () => '/kokpit'
    },
    {
      path: '/kokpit',
      component: Dashboard,
      name: 'Kokpit',
      beforeEnter: (to, from, next) => crmPermissionsCheck('dashboard', next)
    },
    {
      path: '/zadania',
      component: ProcessItems,
      name: 'Zadania do wykonania',
      meta: { layout: 'worker' },
      beforeEnter: (to, from, next) => crmPermissionsCheck('process-items', next)
    },
    {
      path: '/zadania-kierowcy',
      component: DriverItems,
      name: 'Zadania dla kierowcy',
      meta: { layout: 'driver' },
      beforeEnter: (to, from, next) => crmPermissionsCheck('driver_orders', next)
    },
    {
      path: '/procedury',
      component: Procedures,
      name: 'Procedury',
      beforeEnter: (to, from, next) => crmPermissionsCheck('procedures', next)
    },
    {
      path: '/procedury/nowa',
      component: ProcedureForm,
      name: 'Dodawanie procedury',
      beforeEnter: (to, from, next) => crmPermissionsCheck('procedures', next)
    },
    {
      path: '/procedury/edycja/:id',
      component: ProcedureForm,
      name: 'Edycja procedury',
      beforeEnter: (to, from, next) => crmPermissionsCheck('procedures', next)
    },
    {
      path: '/produkty',
      component: Products,
      name: 'Produkty',
      beforeEnter: (to, from, next) => crmPermissionsCheck('products', next)
    },
    {
      path: '/produkty/nowy',
      component: ProductForm,
      name: 'Dodawanie produktu',
      beforeEnter: (to, from, next) => crmPermissionsCheck('products', next)
    },
    {
      path: '/produkty/edycja/:id',
      component: ProductForm,
      name: 'Edycja produktu',
      beforeEnter: (to, from, next) => crmPermissionsCheck('products', next)
    },
    {
      path: '/stany-materialow',
      component: MaterialsStock,
      name: 'Stany materiałów',
      beforeEnter: (to, from, next) => crmPermissionsCheck('materials-stock', next)
    },
    {
      path: '/stany-materialow/produkty',
      component: MaterialsStockProducts,
      name: 'Stany materiałów - Produkty',
      beforeEnter: (to, from, next) => crmPermissionsCheck('materials-stock', next)
    },
    {
      path: '/stany-materialow/produkty/edycja/:id',
      component: MaterialsStockProductForm,
      name: 'Stany materiałów - Produkty - Edycja',
      beforeEnter: (to, from, next) => crmPermissionsCheck('materials-stock', next)
    },
    {
      path: '/stany-materialow/materialy-i-kolory',
      component: MaterialsStockColors,
      name: 'Stany materiałów - Materiały i kolory',
      beforeEnter: (to, from, next) => crmPermissionsCheck('materials-stock', next)
    },
    {
      path: '/stany-materialow/materialy-i-kolory/edycja/:id',
      component: MaterialsStockColorsForm,
      name: 'Stany materiałów - Materiały i kolory - Edycja',
      beforeEnter: (to, from, next) => crmPermissionsCheck('materials-stock', next)
    },
    {
      path: '/stany-materialow/historia-zuzycia',
      component: MaterialsStockUsage,
      name: 'Stany materiałów - Historia zużycia',
      beforeEnter: (to, from, next) => crmPermissionsCheck('materials-stock', next)
    },
    {
      path: '/ustawienia',
      component: Settings,
      name: 'Ustawienia',
      beforeEnter: (to, from, next) => crmPermissionsCheck('settings', next)
    },
    {
      path: '/ustawienia/materialy',
      component: Materials,
      name: 'Materiały',
      beforeEnter: (to, from, next) => crmPermissionsCheck('settings', next)
    },
    {
      path: '/ustawienia/materialy/nowy',
      component: MaterialForm,
      name: 'Dodawanie materiału',
      beforeEnter: (to, from, next) => crmPermissionsCheck('settings', next)
    },
    {
      path: '/ustawienia/materialy/edycja/:id',
      component: MaterialForm,
      name: 'Edycja materiału',
      beforeEnter: (to, from, next) => crmPermissionsCheck('settings', next)
    },
    {
      path: '/ustawienia/nici',
      component: Strands,
      name: 'Nici',
      beforeEnter: (to, from, next) => crmPermissionsCheck('settings', next)
    },
    {
      path: '/ustawienia/nici/nowa',
      component: StrandForm,
      name: 'Dodawanie nici',
      beforeEnter: (to, from, next) => crmPermissionsCheck('settings', next)
    },
    {
      path: '/ustawienia/nici/edycja/:id',
      component: StrandForm,
      name: 'Edycja nici',
      beforeEnter: (to, from, next) => crmPermissionsCheck('settings', next)
    },
    {
      path: '/ustawienia/polary',
      component: Polars,
      name: 'Polary',
      beforeEnter: (to, from, next) => crmPermissionsCheck('settings', next)
    },
    {
      path: '/ustawienia/polary/nowy',
      component: PolarForm,
      name: 'Dodawanie polaru',
      beforeEnter: (to, from, next) => crmPermissionsCheck('settings', next)
    },
    {
      path: '/ustawienia/polary/edycja/:id',
      component: PolarForm,
      name: 'Edycja polaru',
      beforeEnter: (to, from, next) => crmPermissionsCheck('settings', next)
    },
    {
      path: '/ustawienia/przeszycia',
      component: Seams,
      name: 'Przeszycia',
      beforeEnter: (to, from, next) => crmPermissionsCheck('settings', next)
    },
    {
      path: '/ustawienia/przeszycia/nowe',
      component: SeamForm,
      name: 'Dodawanie przeszycia',
      beforeEnter: (to, from, next) => crmPermissionsCheck('settings', next)
    },
    {
      path: '/ustawienia/przeszycia/edycja/:id',
      component: SeamForm,
      name: 'Edycja przeszycia',
      beforeEnter: (to, from, next) => crmPermissionsCheck('settings', next)
    },
    {
      path: '/ustawienia/rozmiary',
      component: Sizes,
      name: 'Rozmiary',
      beforeEnter: (to, from, next) => crmPermissionsCheck('settings', next)
    },
    {
      path: '/ustawienia/rozmiary/nowy',
      component: SizeForm,
      name: 'Dodawanie rozmiaru',
      beforeEnter: (to, from, next) => crmPermissionsCheck('settings', next)
    },
    {
      path: '/ustawienia/rozmiary/edycja/:id',
      component: SizeForm,
      name: 'Edycja rozmiaru',
      beforeEnter: (to, from, next) => crmPermissionsCheck('settings', next)
    },
    {
      path: '/uzytkownicy',
      component: Users,
      name: 'Użytkownicy',
      beforeEnter: (to, from, next) => crmPermissionsCheck('users', next)
    },
    {
      path: '/uzytkownicy/nowy',
      component: UserForm,
      name: 'Dodawanie Użytkownika',
      beforeEnter: (to, from, next) => crmPermissionsCheck('users', next)
    },
    {
      path: '/uzytkownicy/edycja/:id',
      component: UserForm,
      name: 'Edycja użytkownika',
      beforeEnter: (to, from, next) => crmPermissionsCheck('users', next)
    },
    {
      path: '/klienci',
      component: Customers,
      name: 'Klienci',
      beforeEnter: (to, from, next) => crmPermissionsCheck('customers', next)
    },
    {
      path: '/klienci/nowy',
      component: CustomerForm,
      name: 'Dodawanie klienta',
      beforeEnter: (to, from, next) => crmPermissionsCheck('customers', next)
    },
    {
      path: '/klienci/edycja/:id',
      component: CustomerForm,
      name: 'Edycja klienta',
      beforeEnter: (to, from, next) => crmPermissionsCheck('customers', next)
    },
    {
      path: '/zamowienia',
      component: Orders,
      name: 'Zamówienia',
      beforeEnter: (to, from, next) => crmPermissionsCheck('orders', next)
    },
    {
      path: '/zamowienia/nowe',
      component: OrderForm,
      name: 'Dodawanie zamówienia',
      beforeEnter: (to, from, next) => crmPermissionsCheck('orders', next)
    },
    {
      path: '/zamowienia/edycja/:id',
      component: OrderForm,
      name: 'Edycja zamówienia',
      beforeEnter: (to, from, next) => crmPermissionsCheck('orders', next)
    },
    {
      path: '/404',
      component: Errors
    },
    {
      path: '/brak-uprawnien',
      component: NoPermissions
    },
    {
      path: '*',
      redirect: '/404'
    }
  ]
});

router.beforeEach((to, from, next) => {
  let notLoggedInRoutes = [
    'Logowanie',
    'Wyloguj'
  ];

  if (notLoggedInRoutes.indexOf(to.name) === -1 && !SanitizeUtils.getToken()) {
    let splashscreenHtml = document.querySelector('#splashscreen');

    if (splashscreenHtml) {
      splashscreenHtml.parentNode.removeChild(splashscreenHtml);
    }

    next({ name: 'Logowanie' });
    return;
  }

  if (notLoggedInRoutes.indexOf(to.name) !== -1) {
    let splashscreenHtml = document.querySelector('#splashscreen');

    if (splashscreenHtml) {
      splashscreenHtml.parentNode.removeChild(splashscreenHtml);
    }

    next();
    return;
  }

  if (!window.crmApp) {
    setTimeout(() => {
      next({
        name: 'Splashscreen',
        params: {
          nextRoute: to.path
        }
      });

      let splashscreenHtml = document.querySelector('#splashscreen');

      if (splashscreenHtml) {
        splashscreenHtml.parentNode.removeChild(splashscreenHtml);
      }
    }, 0);

    return;
  }

  next();
});

export default router;
